/**
 * ListingsFiltersControl — the filters trigger, its badge, and the modal it
 * opens. ONE component, rendered in two places.
 *
 * THE DEFECT THIS CLOSES (obs. 2, measured on the browse hub): desktop had a
 * sort `<select>` and no way to filter; mobile had filters and no sort. The
 * sort is retired (owner decision — §5 item 2, and the URL vocabulary went
 * with it); the filters are the control that belongs at BOTH viewports, and
 * the reason they only existed at one is structural. The trigger was a bare
 * `<Button onClick>` in `ListingsTopBar` while the `open` state, the badge
 * count and the modal mount all lived in `ListingsClient` — so putting the
 * same trigger in the navbar meant either an `infra → feature` import the
 * element DAG refuses, or a second copy of all three.
 *
 * A control that owns its own disclosure state has no such problem. This one
 * holds `open` (nothing else does), derives its badge from the route and the
 * URL through `useListingsCanvasScope`, and can therefore be MOUNTED
 * anywhere: the navbar's centre slot (`ListingsBrowseControls`, `infra`) and
 * the mobile top bar's right cell (`ListingsTopBar`, `feature`) both reach
 * `entity`, and both get the same badge for the same URL by construction
 * rather than by agreement.
 *
 * ★ Why the badge is `filterBadgeCount` and not a local count: it is the Rust
 * filters SSOT's (`domain_core::filters::active_filter_count`), the same
 * function the modal's own reset affordance keys off. The hand-rolled version
 * this estate shipped once omitted the district, estate and query dimensions
 * entirely, so a search-bar selection showed a badge of zero.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { filterBadgeCount } from '@/shared/lib/listings/listings-filter-set';
import { Button } from '@letsgethome/ui/button';
import { cn } from '@letsgethome/ui/lib/utils';
import { SlidersHorizontal } from 'lucide-react';
import { useCallback, useState } from 'react';

import { ListingsFiltersModal } from './ListingsFiltersModal';
import { useListingsCanvasScope } from './use-listings-browse-scope';

export interface ListingsFiltersControlProps {
  /** Extra classes for the trigger button (placement cosmetics only). */
  className?: string;
}

export function ListingsFiltersControl({ className }: ListingsFiltersControlProps) {
  const t = useTranslations('listings');
  const { filters } = useListingsCanvasScope();
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const count = filterBadgeCount(filters);

  return (
    <>
      <Button
        type="button"
        data-testid="listings-filters-trigger"
        variant="ghost"
        size="icon"
        onClick={handleOpen}
        aria-label={
          count > 0 ? t('filters.buttonLabelWithCount', { count }) : t('filters.buttonLabel')
        }
        className={cn('relative h-10 w-10', className)}
      >
        <SlidersHorizontal className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-highlight text-highlight-foreground text-[10px] font-medium">
            {count}
          </span>
        )}
      </Button>

      {/* The modal's histogram and live results count are scoped to whatever
          the page is browsing — and it reads that scope from the SAME
          derivation the badge above was read from rather than taking it as
          three props, so the preview cannot describe a different search than
          the badge (§2.10: a second mount with a divergent scope is no longer
          representable). */}
      <ListingsFiltersModal open={open} onOpenChange={setOpen} />
    </>
  );
}
